import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  effect,
  HostListener,
  OnInit,
  Signal,
  ViewEncapsulation
} from '@angular/core';
import {Router} from "@angular/router";
import {AuthService} from "./api/auth.service";
import {UserInfo, UserRole} from "./api/model/user";
import {BuildDetailsService} from "./api/build.service";
import {Observable} from "rxjs";
import {AlertService} from "./api/alert.service";
import {AlertGrouped} from "./api/model/alert";
import {BreakpointObserver, Breakpoints} from "@angular/cdk/layout";
import {ProjectType} from "./api/model/project";
import {ManufacturerService} from "./api/manufacturer.service";
import {BelongsTo} from "./api/model/common";

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.sass'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppComponent implements OnInit {
  title = 'Sonus Space';

  alerts: Signal<AlertGrouped[]> = this.alertService.alertsGroupedByEquipmentSignal;
  snoozedAlerts: Signal<AlertGrouped[]> = this.alertService.snoozedAlertsGroupedByEquipmentSignal;

  alertsProjectBadge: Signal<string> = computed(() => {
    const count = this.alerts().filter(a => a.project?.type == ProjectType.PUBLIC).length;
    return count > 0 ? count.toString() : "";
  });
  alertsProductionBadge: Signal<string> = computed(() => {
    const count = this.alerts().filter(a => a.project?.type == ProjectType.PRODUCTION).length;
    return count > 0 ? count.toString() : "";
  });
  alertsSalesBadge: Signal<string> = computed(() => {
    const count = this.alerts().filter(a => a.project?.type == ProjectType.SALES).length;
    return count > 0 ? count.toString() : "";
  });
  alertsServiceBadge: Signal<string> = computed(() => {
    const count = this.alerts().filter(a => a.belongsTo == BelongsTo.RMA).length;
    return count > 0 ? count.toString() : "";
  });
  snoozedAlertsForUser: Signal<AlertGrouped[]> = computed(() => {
    return this.snoozedAlerts().filter(a => a.users.includes(this.authService.currentUserSignal()?.email || ""));
  });
  alertBadge: Signal<string> = computed(() => {
    return this.alerts().length > 0 ? this.alerts().length.toString() : "";
  });

  manufacturerDetailedSignal = this.manufacturerService.listDetailedSignal()
  unassignedManufacturerBadge: Signal<number | undefined> = computed(() => {
    const user = this.authService.currentUserSignal();
    if (user && user.roles.includes(UserRole.ADMIN)) {
      return this.manufacturerDetailedSignal()?.filter(m => !m.assigned).length || undefined;
    }
    return
  })

  buildNumber: string = this.buildDetailsService.buildNumberAtCompile
  buildNumberNew$: Observable<string> = this.buildDetailsService.newBuildNumberDetected
  buildIsUpToDate$: Observable<boolean> = this.buildDetailsService.buildIsUpToDateSubject
  isLoggedIn: Signal<{ value: boolean } | null> = this.authService.isLoggedInSignal;
  portrait: boolean = false;
  searchVisible: boolean = true;
  links = [
    {path: '/integration', label: 'Integration', badge: this.alertsProjectBadge},
    {path: '/sales', label: 'Sales', badge: this.alertsSalesBadge},
    {path: '/service', label: 'Service', badge: this.alertsServiceBadge},
    // {path: '/production', label: 'Production', badge: this.alertsProductionBadge},
    {path: '/purchase-order', label: 'Purchase Orders', badge: null},
    {path: '/equipment', label: 'Equipment', badge: null},
  ];
  protected readonly location = location;
  protected readonly alert = alert;

  constructor(private router: Router,
              private authService: AuthService,
              private manufacturerService: ManufacturerService,
              private responsive: BreakpointObserver,
              private alertService: AlertService,
              private buildDetailsService: BuildDetailsService,
              private _changeDetectorRef: ChangeDetectorRef,
  ) {
    effect(() => {
      console.log(`AppComponent alertsBadge = ${this.alertBadge}, alerts`, this.alerts());
    });

    effect(() => {
      console.log(`AppComponent authService loggedIn fired`, this.isLoggedIn());
      this._changeDetectorRef.markForCheck();
    });
  }

  @HostListener('window:popstate', ['$event'])
  onPopState(event: PopStateEvent) {
    console.log('AppComponent popStateEvent triggerred', event);
    this._changeDetectorRef.markForCheck();
  }

  ngOnInit() {
    this.responsive.observe([
      Breakpoints.HandsetPortrait,
      Breakpoints.TabletPortrait,
    ])
      .subscribe(result => {
        this.searchVisible = true;
        this.portrait = false;
        if (result.matches) {
          this.searchVisible = false;
          this.portrait = true;
        }
      });
  }

  currentUser(): UserInfo | null | undefined {
    return this.authService.currentUser;
  }

  logout() {
    this.authService.logout();
    this.router.navigate(["/login"])
      .then(() => {
        window.location.reload();
      });
  }

  showSearch() {
    this.searchVisible = true;
  }

  navigateToOnlyWithAlerts(link: string) {
    this.router.navigateByUrl(`${link}?onlyWithAlerts=true`, {skipLocationChange: true});
  }
}
